import React from 'react';

import voca from 'voca';

import Checkbox from 'components/application/Checkbox';
import ProfilePhotoDisplay from 'components/application/ProfilePhotoDisplay';

import ReviewStatusIndicator from 'components/contentRegister/components/ReviewStatusIndicator';
import SharedContentBadge from 'components/contentRegister/components/SharedContentBadge';
import DragHandle from 'components/contentRegister/components/sortableTable/components/DragHandle';
import { riskAssessmentPath } from 'components/contentRegister/helpers/paths';

import { formatDate } from 'components/helpers/dates';
import { personDisplayName } from 'components/helpers/users';

export default function buildRiskAssessmentColumns({ riskRegisterPolicy }) {
  return [
    {
      id: 'dragHandle',
      header: '',
      cell: ({ row, table }) => {
        const { included, translations } = table.options.meta;

        return (
          <div className='tw-flex tw-items-center tw-justify-center'>
            <DragHandle
              label={translations.reorderName(
                included.masterActivity[
                  row.original.relationships.masterActivity.data.id
                ].attributes.name,
              )}
            />
          </div>
        );
      },
      meta: { classNames: 'tw-pl-5 tw-w-12.5' },
    },
    riskRegisterPolicy.canShow && {
      id: 'select',
      header: '',
      cell: ({ row, table }) => {
        const {
          included,
          onMasterActivityToggle,
          selectedMasterActivityIds,
          translations,
        } = table.options.meta;

        const {
          attributes: { name },
          id: masterActivityId,
        } =
          included.masterActivity[
            row.original.relationships.masterActivity.data.id
          ];

        return (
          <div
            className='tw-flex tw-items-center'
            onClick={(event) => event.stopPropagation()}
          >
            <Checkbox
              isChecked={selectedMasterActivityIds.has(masterActivityId)}
              label={translations.selectName(name)}
              name='select-risk-assessment'
              onChange={() => onMasterActivityToggle(masterActivityId)}
            />
          </div>
        );
      },
      meta: { classNames: 'tw-w-5.5' },
    },
    riskRegisterPolicy.canShow && {
      id: 'reference',
      header: ({ table }) => table.options.meta.translations.ref,
      cell: ({ row, table }) => (
        <div className='tw-truncate'>
          {
            table.options.meta.included.masterActivity[
              row.original.relationships.masterActivity.data.id
            ].attributes.reference
          }
        </div>
      ),
      meta: { classNames: 'tw-p-2 tw-w-22 tw-text-left' },
    },
    {
      id: 'name',
      header: ({ table }) => table.options.meta.translations.riskAssessment,
      cell: ({ row, table }) => {
        const { included, onNavigate, tradeId } = table.options.meta;

        const {
          attributes: { name },
          id: masterActivityId,
        } =
          included.masterActivity[
            row.original.relationships.masterActivity.data.id
          ];

        return (
          <a
            className='tw-block tw-truncate tw-font-medium tw-text-grey-900 hover:tw-text-grey-900'
            data-element-name='risk-assessment-name'
            href={riskAssessmentPath(tradeId, masterActivityId)}
            onClick={(event) => {
              event.stopPropagation();
              onNavigate(row.original.id);
            }}
          >
            {name}
          </a>
        );
      },
      meta: { classNames: 'tw-p-2 tw-text-left' },
    },
    riskRegisterPolicy.canShow && {
      id: 'status',
      header: ({ table }) => table.options.meta.translations.status,
      cell: ({ row, table }) => (
        <div className='tw-flex tw-items-center tw-justify-center'>
          <ReviewStatusIndicator
            status={voca.camelCase(
              table.options.meta.included.masterActivity[
                row.original.relationships.masterActivity.data.id
              ].attributes.reviewDateStatus,
            )}
            variant='riskAssessment'
          />
        </div>
      ),
      meta: { classNames: 'tw-p-2 tw-w-15 tw-text-center' },
    },
    {
      id: 'shared',
      header: '',
      cell: ({ row, table }) => {
        const {
          attributes: { name },
          meta: { areAllSharedCategoriesVisible, isSharedAcrossCategories },
          relationships: { sharedCategories },
        } = table.options.meta.included.masterActivity[
          row.original.relationships.masterActivity.data.id
        ];

        if (!isSharedAcrossCategories) return null;

        return (
          <div className='tw-flex tw-items-center tw-justify-center'>
            <SharedContentBadge
              areAllVisible={areAllSharedCategoriesVisible}
              recordName={name}
              sharedNames={sharedCategories.data.map(
                ({ id: tradeId }) =>
                  table.options.meta.included.trade[tradeId].attributes.name,
              )}
              variant='categories'
            />
          </div>
        );
      },
      meta: { classNames: 'tw-p-2 tw-w-46' },
    },
    riskRegisterPolicy.canShow && {
      id: 'reviewDate',
      header: ({ table }) => table.options.meta.translations.reviewDate,
      cell: ({ row, table }) => {
        const { reviewDate } =
          table.options.meta.included.masterActivity[
            row.original.relationships.masterActivity.data.id
          ].attributes;

        if (!reviewDate) return null;

        return (
          <div className='tw-truncate'>
            {formatDate({ date: reviewDate, format: 'dd MMM yyyy' })}
          </div>
        );
      },
      meta: { classNames: 'tw-p-2 tw-w-32 tw-text-left' },
    },
    riskRegisterPolicy.canShow && {
      id: 'assignee',
      header: ({ table }) => table.options.meta.translations.assignedTo,
      cell: ({ row, table }) => {
        const { included } = table.options.meta;

        const assignee =
          included.user[
            included.masterActivity[
              row.original.relationships.masterActivity.data.id
            ].relationships.assignee.data?.id
          ];

        if (!assignee) return null;

        const assigneePersonnel =
          included.personnel[assignee.relationships.personnel.data?.id];

        const profilePhotoUrl =
          included.profilePhoto[
            assigneePersonnel?.relationships.profilePhoto.data?.id
          ]?.links.croppedUrl;

        return (
          <div className='tw-flex tw-items-center'>
            <div className='tw-mr-2'>
              <ProfilePhotoDisplay
                componentSize='sm'
                photoUrl={profilePhotoUrl}
                profileInitials={`${assignee.attributes.firstName[0]}${assignee.attributes.lastName[0]}`}
              />
            </div>
            <span className='tw-truncate'>{personDisplayName(assignee)}</span>
          </div>
        );
      },
      meta: { classNames: 'tw-p-2 tw-w-46 tw-text-left' },
    },
  ].filter(Boolean);
}
